import getResponseArray from '@tymbe/utils/getResponseArray';
import classNames from 'classnames';
import { FieldState, useFormApi, useFormState } from 'informed';
import { useCallback } from 'react';
import { useQuery } from 'react-query';

import ComboBox from './Combobox';
import apiClient from '../../../apiClient';
import { CompanyData } from '../../../types/TymbeApi';
import { BranchofficeData } from '@tymbe/schema/branchoffice.interface';

type SelectorForm = {
  company: number;
  branchoffice: number;
  department: number;
};

type CompanyBranchSelectorFieldsProps = {
  isFloating: boolean;
  showLabels?: boolean;
  start?: string;
  end?: string;
}

const CompanyBranchSelectorFields = ({
  isFloating,
  showLabels = true,
  start,
  end,
}: CompanyBranchSelectorFieldsProps) => {
  const formApi = useFormApi<SelectorForm>();
  const { values } = useFormState<SelectorForm>();
  const { data, isLoading } = useQuery({
    queryKey: ['company', 'branchoffice', 'department', 'list'],
    queryFn: async () => {
      const companies = getResponseArray(await apiClient.service('company').find({
        query: {
          $limit: -1,
        }
      }));

      const branchoffices = getResponseArray(await apiClient.service('branchoffice').find({
        query: {
          $limit: -1,
        }
      }));

      branchoffices.forEach(branchoffice => {
        if (branchoffice.parent_id) {
          const parentBranchoffice = branchoffices.find(b => b.id === branchoffice.parent_id);
          if (parentBranchoffice) {
            parentBranchoffice.child = [ ...(parentBranchoffice.child || []), branchoffice ];
          }
        } else {
          const company = companies.find(company => company.id === branchoffice.company_id);
          if (company) {
            company.branchoffice = [ ...(company.branchoffice || []), branchoffice ];
          }
        }
      });

      return companies;
    },
    refetchOnWindowFocus: false,
  });
  const { data: shifts, isLoading: isShiftsLoading } = useQuery({
    queryKey: ['branchofficeIdList', start, end],
    queryFn: async () => {
      return getResponseArray(await apiClient.service('shift').find({
        query: {
          $select: ['branchoffice_id', 'company_id'],
          $limit: -1,
          $and: [
            { start_time: { $gte: start } },
            { start_time: { $lte: end } },
          ]
        },
      }));
    },
    refetchOnWindowFocus: false,
  });
  const companies = getResponseArray(data);

  const selectedCompany = companies.find(
    (company) => company.id === Number(values.company)
  );
  const branchoffices = selectedCompany?.branchoffice || [];
  const selectedBranchoffice = branchoffices.find(
    (branchoffice) => branchoffice.id === Number(values.branchoffice)
  );
  const departments = selectedBranchoffice?.child || [];

  const onCompanyChange = useCallback(
    ({ value }: FieldState<number>) => {
      const company = companies.find((c) => c.id === Number(value));
      const branchoffice =
        company?.branchoffice?.length === 1
          ? company?.branchoffice?.[0]
          : { id: undefined };
      if (branchoffice?.id === Number(formApi.getValue('branchoffice'))) {
        return;
      }
      formApi.setValue('branchoffice', branchoffice?.id);
    },
    [companies, formApi]
  );

  const onBranchofficeChange = useCallback(
    ({ value }: FieldState<number>) => {
      const company = companies.find(
        (c) => c.id === Number(formApi.getValue('company'))
      );
      const branchoffice = company?.branchoffice?.find(
        (b) => b.id === Number(value)
      );
      const department =
        branchoffice?.child?.length === 1
          ? branchoffice.child[0]
          : { id: undefined };
      if (department.id === Number(formApi.getValue('department'))) {
        return;
      }
      formApi.setValue('department', department.id);
    },
    [companies, formApi]
  );

  const getShiftCount = useCallback((option: CompanyData | BranchofficeData) => {
    if (isShiftsLoading) return 0;
    if ('parent_id' in option) {
      let count = shifts?.filter(shift => shift.branchoffice_id === option.id).length || 0;
      count += option.child?.reduce((acc, child) => {
        return acc + (shifts?.filter(shift => shift.branchoffice_id === child.id).length || 0);
      }, 0) || 0;
      return count;
    }

    return shifts?.filter(shift => shift.company_id === option.id).length || 0;
  }, [isShiftsLoading, shifts]);

  const getOptionLabel = useCallback(
    (option: CompanyData | BranchofficeData) => `[${option.id}] ${option.name} (${!isShiftsLoading ? getShiftCount(option) : '...'})`,
    [getShiftCount, isShiftsLoading]
  );
  const getOptionValue = useCallback(
    (option: CompanyData | BranchofficeData) => option.id,
    []
  );
  const getValueOption = useCallback(
    (value: number, options: (CompanyData | BranchofficeData)[]) =>
      options.find((option) => option.id === Number(value)),
    []
  );

  const sortByShiftCountName = useCallback(
    (a: CompanyData | BranchofficeData, b: CompanyData | BranchofficeData) => {
      const shiftCountA = getShiftCount(a) ? 1 : 0;
      const shiftCountB = getShiftCount(b) ? 1 : 0;
      if (shiftCountA !== shiftCountB) {
        return shiftCountB - shiftCountA; // Sort by shift count descending
      }
      return a.name.localeCompare(b.name); // Sort by name if shift counts are equal
    },
    [getShiftCount]
  );

  const getInitialCount = useCallback(
    (options: (CompanyData | BranchofficeData)[]) => {
      const count = options.filter(getShiftCount).length;
      return count < 5 ? 5 : count; // Ensure at least 5 items are shown initially
    },
    [getShiftCount]
  );

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div
      className={classNames(
        'w-full flex gap-4',
        isFloating ? 'flex-col' : 'flex-row'
      )}
    >
      <ComboBox
        label={showLabels ? "Společnost" : undefined}
        name="company"
        placeholder="Všechny firmy"
        className="flex-grow"
        inputClassName="border w-full h-fill rounded-lg border-secondary-300 text-body-2-xl p-2 truncate"
        menuClassName="flex flex-col bg-bg w-full leading-normal my-1 shadow-down-md rounded-lg py-2 z-50 max-h-96 overflow-y-auto"
        itemClassName="flex items-center text-body-2-xl px-2 min-h-8 hover:bg-secondary-50 [&.selected]:bg-secondary-50 text-secondary-600"
        footerClassName="h-6 px-2 text-caption-xl text-gray-500 text-right border-t border-secondary-100 flex justify-between items-center"
        placeholderClassName="text-secondary-300"
        options={companies.sort(sortByShiftCountName)}
        onChange={onCompanyChange}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        getValueOption={getValueOption}
        initialCount={getInitialCount}
      />

      <ComboBox
        label={showLabels ? "Provozovna" : undefined}
        name="branchoffice"
        placeholder="Všechny pobočky"
        className={classNames('flex-grow')}
        inputClassName="border w-full h-fill rounded-lg border-secondary-300 text-body-2-xl p-2 truncate"
        menuClassName="flex flex-col bg-bg w-full leading-normal my-1 shadow-down-md rounded-lg py-2 z-50 max-h-96 overflow-y-auto"
        itemClassName="flex items-center text-body-2-xl px-2 min-h-8 hover:bg-secondary-50 [&.selected]:bg-secondary-50 text-secondary-600"
        footerClassName="h-6 px-2 text-caption-xl text-gray-500 text-right border-t border-secondary-100 flex justify-between items-center"
        placeholderClassName="text-secondary-300"
        options={branchoffices.sort(sortByShiftCountName)}
        onChange={onBranchofficeChange}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        getValueOption={getValueOption}
        initialCount={getInitialCount}
      />

      <ComboBox
        label={showLabels ? "Oddělení" : undefined}
        name="department"
        placeholder="Všechna oddělení"
        className={classNames('flex-grow')}
        inputClassName="border w-full h-fill rounded-lg border-secondary-300 text-body-2-xl p-2 truncate"
        menuClassName="flex flex-col bg-bg w-full leading-normal my-1 shadow-down-md rounded-lg py-2 z-50 max-h-96 overflow-y-auto"
        itemClassName="flex items-center text-body-2-xl px-2 min-h-8 hover:bg-secondary-50 [&.selected]:bg-secondary-50 text-secondary-600"
        footerClassName="h-6 px-2 text-caption-xl text-gray-500 text-right border-t border-secondary-100 flex justify-between items-center"
        placeholderClassName="text-secondary-300"
        options={departments.sort(sortByShiftCountName)}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        getValueOption={getValueOption}
        initialCount={getInitialCount}
      />
    </div>
  );
};

export default CompanyBranchSelectorFields;
